<template>
    <section v-if="document" class="container min-h-full blog-layout gap-4">
        <CmsDocument :document>
            <ButtonGroup v-if="hasPages" class="my-8">
                <Button v-for="page in pages" :key="page.number" as-child v-bind="pageButtonProperties(page)">
                    <NuxtLink :to="page.path">{{ page.number }}</NuxtLink>
                </Button>
            </ButtonGroup>
        </CmsDocument>
        <CmsNavigation :navigation :root>
            <h3>
                <NuxtLinkLocale to="/blog">{{ $t("StaticSiteStrings.blog_title") }}</NuxtLinkLocale>
            </h3>
            <CmsSearch search-type="blog" :root :title="$t('StaticSiteStrings.blog_placeholder')" />
            <h4>{{ $t("StaticSiteStrings.blog_popularTags") }}</h4>
            <div class="flex gap-2 flex-wrap">
                <NuxtLink
                    v-for="tag in pagination?.tags"
                    :key="tag.path"
                    :to="tag.path"
                    class="text-sm bg-gray-200 border border-gray-300 px-2 rounded-xl">
                    {{ tag.title }}
                </NuxtLink>
            </div>
            <h4 class="mb-1">{{ $t("StaticSiteStrings.blog_recentPosts") }}</h4>
        </CmsNavigation>
    </section>
</template>
<script setup lang="ts">
import type { ButtonVariants } from "~/components/ui";

type BlogPage = { number: number; path: string };
type BlogTag = { title: string; path: string; pages: BlogPage[] };

type BlogPagination = {
    pages: BlogPage[];
    tags: BlogTag[];
};

const route = useRoute();
const { locale } = useI18n();
const localePath = useLocalePath();

const root = localePath("/blog/");
const documentPath = getCMSDocumentPath("blog", locale.value, route.params.path);
const paginationPath = getCMSDocumentPath("blog", locale.value, "pagination.json");
const navigationPath = getCMSDocumentPath("blog", locale.value, "recent-posts.json");

const [{ data: document, error: docError }, { data: navigation }, { data: pagination }] = await Promise.all([
    useFetch<CMSDocument>(documentPath),
    useFetch<CMSNavItem[]>(navigationPath),
    useFetch<BlogPagination>(paginationPath),
]);

if (docError.value) {
    throw showError(docError.value);
}

const { title, description } = document.value ?? {};
useSeoMeta({ title, ogTitle: title, description, ogDescription: description });

const isBlogHome = computed(() => !route.params.path || route.params.path[0] === "page");

const isBlogTag = computed(() => route.params.path && route.params.path[0] === "tag");

const currentTag = computed(() =>
    isBlogTag.value && pagination.value ? pagination.value.tags.find((t) => route.path.startsWith(t.path)) : null
);

const pages = computed(() => {
    if (!pagination.value) return;
    if (isBlogHome.value) return pagination.value.pages;
    return currentTag.value?.pages;
});

const hasPages = computed(() => Array.isArray(pages.value) && pages.value.length > 1);

function pageButtonProperties(page: BlogPage): ButtonVariants {
    return route.path === page.path ? { fill: "solid" } : {};
}
</script>
<style scoped>
.blog-layout {
    display: grid;

    @media (width >= 48rem) {
        grid-template-columns: minmax(0, 1fr) calc(var(--spacing) * 56);
    }

    @media (width >= 64rem) {
        grid-template-columns: minmax(0, 1fr) calc(var(--spacing) * 80);
    }
}
</style>
